import styled from "styled-components";
import { DesignTokens, Text } from "melco-ui";
import { SELECTED_BG, SELECTED_ACCENT } from "../summitTheme";

const { spacings, colors } = DesignTokens;

// WHAT: fixed card height, independent of which optional fields (email,
// business name, customer id, sub-user chip) are actually present.
// WHY: without a min-height, a card missing all of the optional fields
// collapses to just its primary line, while a fully-populated card is
// noticeably taller — rows in the list end up visibly uneven. Pairing
// this min-height with the "always render the slot" approach below
// (see SecondaryLine/MetaRow) is what actually locks the height in,
// since min-height alone wouldn't stop varying content from expanding
// a *taller* card even further.
const CARD_MIN_HEIGHT = 82;

const Card = styled.div<{ $selected?: boolean }>`
  min-height: ${CARD_MIN_HEIGHT}px;
  box-sizing: border-box;
  padding: ${spacings.small}px ${spacings.middle}px;
  border-bottom: 1px solid ${colors.gray.light};
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: background-color 0.1s ease, border-color 0.1s ease;

  &:hover {
    background-color: ${colors.gray.light};
  }

  /* Selected state: a visible red tint + left accent bar, matching the
     "active item" treatment used elsewhere in the app (e.g. the
     highlighted row on System > Locale Strings) — a plain gray
     background alone was too subtle to register as "selected" at a
     glance. */
  ${({ $selected }) =>
    $selected &&
    `
      background-color: ${SELECTED_BG};
      border-left-color: ${SELECTED_ACCENT};
    `}
`;

const PrimaryLine = styled(Text) <{ $selected?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  display: block;
  /* !important is required here: PrimaryLine wraps melco-ui's Text
     component (not a plain element), and Text applies its own color
     internally. Without !important that internal color can win the
     cascade regardless of our rule's position in the stylesheet —
     which is why the label was going invisible on selection instead
     of turning red. */
  ${({ $selected }) =>
    $selected &&
    `
      color: ${SELECTED_ACCENT} !important;
    `}
`;

// WHAT: the email line under the name.
// WHY min-height instead of conditionally rendering the whole element:
// this line is now always mounted (see usage below, rendering a
// non-breaking space when there's no email) so its height is reserved
// whether or not this particular user has an email on file.
const SecondaryLine = styled.div<{ $selected?: boolean }>`
  min-height: 15px;
  font-size: 12px;
  color: ${colors.gray.dark};
  margin-top: 2px;
  word-break: break-all;
  /* !important is required here: PrimaryLine wraps melco-ui's Text
     component (not a plain element), and Text applies its own color
     internally. Without !important that internal color can win the
     cascade regardless of our rule's position in the stylesheet —
     which is why the label was going invisible on selection instead
     of turning red. */
  ${({ $selected }) =>
    $selected &&
    `
      color: ${SELECTED_ACCENT} !important;
    `}
`;

// WHAT: the row of chips (business name, customer id, sub-user).
// WHY min-height: same reasoning as SecondaryLine above — a user with
// zero chips would otherwise leave this row at 0px tall, making their
// card shorter than one with chips. The min-height matches a single
// Chip's rendered height (font-size 11 + 1px vertical padding on each
// side + border), so a chip-less card still reserves that row's space.
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
  min-height: 18px;
  flex-wrap: wrap;
`;

const Chip = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 1px 7px;
  border-radius: 10px;
  border: 1px solid ${colors.gray.light};
  background: ${colors.gray.superLight};
  color: ${colors.gray.dark};
`;

type UserResultCardProps = {
  primaryText: string;
  /** Optional — only the docs say this is a real field; omitted entirely when not present. */
  email?: string;
  /** Real field on the user record — when set, this user reports to a parent/manager account. */
  parentUserId?: string;
  /** Optional business/company name chip. */
  businessName?: string;
  selected?: boolean;
  onClick?: () => void;
  "data-cy-id"?: string;
  melcoCustomerId?: string;
};

export const UserResultCard: React.FC<UserResultCardProps> = ({
  primaryText,
  email,
  parentUserId,
  businessName,
  selected,
  melcoCustomerId,
  onClick,
  ...rest
}) => {
  return (
    <Card $selected={selected} onClick={onClick} {...rest}>
      <PrimaryLine $selected={selected} ellipsis={{ tooltip: primaryText }}>
        {primaryText}
      </PrimaryLine>
      {/* Always rendered (even without an email) so every card reserves
          the same amount of vertical space — see SecondaryLine's
          min-height above. A bare "&nbsp;" keeps the line box present
          without showing stray text to the rep. */}
      <SecondaryLine $selected={selected}>{email || "\u00A0"}</SecondaryLine>

      <MetaRow>
        {businessName && <Chip>{businessName}</Chip>}
        {melcoCustomerId && <Chip>{melcoCustomerId}</Chip>}
        {parentUserId && (<Chip>Sub-user </Chip>)}
      </MetaRow>

    </Card>
  );
};
